"use client";

import Image from "next/image";
import { memo } from "react";

import { Badge } from "@workspace/ui/components/badge";
import { Card } from "@workspace/ui/components/card";

import { useAnalytics } from "~/lib/analytics";
import { getDocModeIcon } from "~/lib/constants/doc-modes";

import type { PROJECTS } from "./constants/projects";

interface ProjectProps {
  project: (typeof PROJECTS)[number];
}

const Tag = ({ tag }: { tag: string }) => {
  const Icon = getDocModeIcon(tag);

  return (
    <Badge className="border px-1.5 capitalize" variant="outline">
      {Icon && <Icon className="size-3.5" />}
      <span className="mr-0.5 ml-1.5 inline-block">{tag}</span>
    </Badge>
  );
};

export const Project = memo<ProjectProps>(({ project }) => {
  const { track } = useAnalytics();

  return (
    <a
      href={`${project.href}?ref=turbostarter`}
      rel="noopener noreferrer"
      target="_blank"
      key={project.name}
      className="group/hover"
      onClick={() => track("showcase_project_click", { project: project.name })}
    >
      <Card className="group-hover/hover:bg-muted/30 flex h-full w-72 flex-col gap-3 p-3 transition-colors sm:min-w-80 md:w-[22rem] md:gap-4 md:p-4 lg:w-[27rem]">
        <div className="relative aspect-[128/80] h-full w-full rounded-lg">
          <Image
            src={project.image}
            alt={project.name}
            fill
            sizes="(max-width: 640px) 288px, (max-width: 768px) 320px, (max-width: 1024px) 352px, 432px"
            className="rounded-lg object-cover"
            unoptimized
          />
        </div>

        <div className="flex flex-col gap-1 px-1 md:gap-1.5">
          <div className="flex w-full flex-wrap items-center justify-between gap-1.5 sm:gap-2.5 md:gap-4">
            <h3 className="font-medium sm:text-lg md:text-xl">
              {project.name}
            </h3>
            <div className="flex items-center justify-start gap-1 md:gap-1.5">
              {project.tags.map((tag) => (
                <Tag key={tag} tag={tag} />
              ))}
            </div>
          </div>
          <p className="text-muted-foreground text-sm text-pretty">
            {project.description}
          </p>
        </div>
      </Card>
    </a>
  );
});
